import { translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { EventType, GetActivityHistoryQuery } from '../../client/generated/graphql';
import { formatTimeSpent, getEventColor } from '../../helpers/activity';
import { getColor } from '../../helpers/color';
import { toDueDate } from '../../helpers/datetime';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';

const getEventIcon = (eventType: EventType) => {
    switch (eventType) {
        case EventType.QuestCompleted:
        case EventType.QuestMetricIncreased:
            return 'map';
        case EventType.GoalCompleted:
            return 'flag';
        case EventType.RewardPurchased:
            return 'shopping-bag';
        case EventType.AchievementAchieved:
            return 'emoji-events';
        default:
            return 'circle';
    }
};

const formatDateTime = (dateTime: string, timeSpent?: string | null) => {
    const baseDateTime = `${dayjs(dateTime).format('HH:mm')} • ${toDueDate(dayjs(dateTime))}`;
    return timeSpent ? `${baseDateTime} • ${timeSpent}` : baseDateTime;
};

type ActivityMaps = {
    questMap: Record<string, any>;
    goalMap: Record<string, any>;
    categoryMap: Record<string, any>;
};

type ActivityEventCellProps = {
    item: GetActivityHistoryQuery['eventLogs'][0];
    showDivider: boolean;
    questNameMap: Record<string, string>;
    goalNameMap: Record<string, string>;
    rewardNameMap: Record<string, string>;
    maps: ActivityMaps;
};

export const ActivityEventCell = ({ item, showDivider, questNameMap, goalNameMap, rewardNameMap, maps }: ActivityEventCellProps) => {
    const { t } = useTranslation();
    const getEventTitle = () => {
        switch (item.eventType) {
            case EventType.QuestCompleted: {
                const questName =
                    item.questCompleted?.questId && questNameMap[item.questCompleted.questId]
                        ? questNameMap[item.questCompleted.questId]
                        : item.questCompleted?.questId;
                return questName || 'Quest completed';
            }
            case EventType.QuestMetricIncreased: {
                const experienceMetric = item.questMetricIncreased.find(metric => metric.questMetricType === 'Experience');
                return experienceMetric ? questNameMap[experienceMetric.questId] || experienceMetric.questId : 'Quest updated';
            }
            case EventType.GoalCompleted: {
                if (!item.goalCompleted) return 'Goal completed';
                const goalName = goalNameMap[item.goalCompleted.goalId] || item.goalCompleted.goalId;
                return `${goalName} Level ${item.goalCompleted.levelCompleted}`;
            }
            case EventType.RewardPurchased:
                return item.rewardPurchased ? rewardNameMap[item.rewardPurchased.rewardId] || item.rewardPurchased.rewardId : 'Reward';
            default:
                return 'Activity';
        }
    };

    const rewardInfo = (() => {
        switch (item.eventType) {
            case EventType.QuestCompleted:
            case EventType.QuestMetricIncreased: {
                const metric = item.questMetricIncreased.find(value => value.questMetricType === 'Experience');
                return metric ? { value: metric.addedValue, type: 'XP' } : null;
            }
            case EventType.GoalCompleted:
                return item.goalCompleted ? { value: item.goalCompleted.currencyEarned, type: 'coins' } : null;
            case EventType.RewardPurchased:
                return item.rewardPurchased ? { value: item.rewardPurchased.currencyPaid, type: 'coins' } : null;
            default:
                return null;
        }
    })();

    const timeSpent =
        item.eventType === EventType.QuestCompleted || item.eventType === EventType.QuestMetricIncreased
            ? formatTimeSpent(null, item.questMetricIncreased.find(metric => metric.questMetricType === 'Time')?.addedValue)
            : null;

    const categoryId = (() => {
        switch (item.eventType) {
            case EventType.QuestCompleted:
            case EventType.QuestMetricIncreased: {
                const questId = item.questCompleted?.questId || item.questMetricIncreased[0]?.questId;
                return questId && maps.questMap[questId] ? maps.questMap[questId].categoryId : null;
            }
            case EventType.GoalCompleted: {
                const goal = item.goalCompleted && maps.goalMap[item.goalCompleted.goalId];
                return goal?.categoryId ?? null;
            }
            default:
                return null;
        }
    })();
    const category = categoryId ? maps.categoryMap[categoryId] : null;

    const isXpReward = rewardInfo?.type === 'XP';
    const rewardLabel = rewardInfo
        ? `${item.eventType === EventType.RewardPurchased ? '-' : '+'}${rewardInfo.value} ${rewardInfo.type}`
        : null;

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
                p: 1.5,
                minWidth: 0,
                borderTop: showDivider ? '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)' : 'none',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    backgroundColor: getEventColor(item.eventType),
                }}
            >
                <CommonIcon icon={getEventIcon(item.eventType)} type="filled" size={20} color="var(--tg-theme-button-text-color)" />
            </Box>
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Text
                        style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1.25,
                        }}
                    >
                        {getEventTitle()}
                    </Text>
                    {isXpReward ? (
                        <Caption
                            style={{
                                color: 'var(--tg-theme-button-color)',
                                flexShrink: 0,
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {rewardLabel}
                        </Caption>
                    ) : rewardInfo ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 999,
                                backgroundColor: getEventColor(item.eventType),
                                color:
                                    item.eventType === EventType.GoalCompleted || item.eventType === EventType.RewardPurchased
                                        ? '#000'
                                        : 'var(--tg-theme-button-text-color)',
                                fontFamily: 'inherit',
                                fontSize: 10,
                                fontWeight: 700,
                                lineHeight: 1.2,
                            }}
                        >
                            {rewardLabel}
                        </Box>
                    ) : null}
                </Stack>
                <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.3 }}>
                    {formatDateTime(item.createdAt, timeSpent)}
                </Caption>
                {category && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '4px',
                                backgroundColor: getColor(category.iconColor),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CategoryIcon
                                icon={category.icon}
                                iconColor={category.iconColor}
                                customIcon={category.customIcon}
                                type="filled"
                                size={10}
                                color="var(--tg-theme-text-color)"
                            />
                        </Box>
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11 }}>{translateTemplateText(t, category.templateId, 'name', category.name)}</Caption>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
